import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Zap, ArrowRight, ArrowLeft, CheckCircle2, Sparkles,
  Target, Calendar, Users, Flame
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const DailyDropOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    niche: '',
    customNiche: '',
    platforms: [],
    postingGoal: '',
    experience: ''
  });

  const niches = [
    { id: 'fitness', label: 'Fitness & Health', emoji: '💪' },
    { id: 'food', label: 'Food & Cooking', emoji: '🍳' },
    { id: 'tech', label: 'Technology', emoji: '💻' },
    { id: 'fashion', label: 'Fashion & Beauty', emoji: '👗' },
    { id: 'travel', label: 'Travel', emoji: '✈️' },
    { id: 'business', label: 'Business & Finance', emoji: '💼' },
    { id: 'entertainment', label: 'Entertainment', emoji: '🎬' },
    { id: 'education', label: 'Education', emoji: '📚' },
    { id: 'lifestyle', label: 'Lifestyle', emoji: '🌟' },
    { id: 'gaming', label: 'Gaming', emoji: '🎮' },
    { id: 'music', label: 'Music', emoji: '🎵' },
    { id: 'art', label: 'Art & Design', emoji: '🎨' },
    { id: 'other', label: 'Other', emoji: '✨' },
  ];

  const platforms = [
    { id: 'instagram', label: 'Instagram', color: 'from-pink-500 to-purple-500' },
    { id: 'tiktok', label: 'TikTok', color: 'from-gray-800 to-gray-600' },
    { id: 'youtube', label: 'YouTube', color: 'from-red-600 to-red-500' },
    { id: 'twitter', label: 'X (Twitter)', color: 'from-gray-700 to-gray-500' },
    { id: 'facebook', label: 'Facebook', color: 'from-blue-600 to-blue-500' },
  ];

  const postingGoals = [
    { id: 'daily', label: 'Every day', description: 'Build maximum consistency', icon: Flame },
    { id: 'weekdays', label: '5 days/week', description: 'Weekdays only', icon: Calendar },
    { id: 'few', label: '2-3 days/week', description: 'Quality over quantity', icon: Target },
    { id: 'weekly', label: 'Once a week', description: 'Just getting started', icon: Users },
  ];

  const experienceLevels = [
    { id: 'beginner', label: "I'm new to content creation", description: "We'll guide you step by step" },
    { id: 'intermediate', label: 'I post sometimes', description: "Let's make you consistent" },
    { id: 'experienced', label: 'I post regularly', description: "We'll help you scale" },
    { id: 'pro', label: "I'm a full-time creator", description: "Advanced tools for pros" },
  ];

  const handleNicheSelect = (nicheId) => {
    setFormData(prev => ({ ...prev, niche: nicheId }));
  };

  const handlePlatformToggle = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const handleNext = () => {
    if (step === 1 && !formData.niche) {
      toast.error('Please select your niche');
      return;
    }
    if (step === 2 && formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    if (step === 3 && !formData.postingGoal) {
      toast.error('Please select your posting goal');
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleComplete = async () => {
    if (!formData.experience) {
      toast.error('Please select your experience level');
      return;
    }

    setIsSubmitting(true);
    try {
      const onboardingData = {
        niche: formData.niche === 'other' ? formData.customNiche : formData.niche,
        platforms: formData.platforms,
        postingGoal: formData.postingGoal,
        experience: formData.experience,
        onboardingCompleted: true
      };

      await api.post('/daily-drop/onboarding', onboardingData);
      toast.success('Welcome to DailyDrop AI! Let\'s start creating!');
      navigate('/daily-drop');
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Failed to save preferences. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="bg-gradient-to-r from-purple-600 to-amber-500 p-3 rounded-xl">
              <Zap className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome to DailyDrop AI</h1>
          <p className="text-purple-200">Let's personalize your experience in 4 quick steps</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s} 
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all ${
                  s < step ? 'bg-green-500 text-white' :
                  s === step ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                  'bg-purple-500/20 text-purple-300'
                }`}
              >
                {s < step ? <CheckCircle2 className="h-5 w-5" /> : s}
              </div>
            ))}
          </div>
          <div className="h-2 bg-purple-500/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Content */}
        <Card className="bg-black/40 border-purple-500/30 backdrop-blur-sm">
          <CardContent className="p-8">
            {/* Step 1: Niche Selection */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-niche">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">What's your content niche?</h2>
                  <p className="text-purple-200">This helps our AI generate better content for you</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {niches.map((niche) => (
                    <button
                      key={niche.id}
                      onClick={() => handleNicheSelect(niche.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        formData.niche === niche.id
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-purple-500/20 hover:border-purple-500/50 bg-purple-500/5'
                      }`}
                      data-testid={`niche-${niche.id}`}
                    >
                      <span className="text-2xl mb-2 block">{niche.emoji}</span>
                      <span className="text-white font-medium">{niche.label}</span>
                    </button>
                  ))}
                </div>
                {formData.niche === 'other' && (
                  <Input
                    value={formData.customNiche}
                    onChange={(e) => setFormData(prev => ({ ...prev, customNiche: e.target.value }))}
                    placeholder="Enter your niche..."
                    className="bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                    data-testid="custom-niche-input"
                  />
                )}
              </div>
            )}

            {/* Step 2: Platform Selection */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-platforms">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">Where do you want to post?</h2>
                  <p className="text-purple-200">Select all platforms you use (you can change this later)</p>
                </div>
                <div className="space-y-3">
                  {platforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformToggle(platform.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between ${
                        formData.platforms.includes(platform.id)
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-purple-500/20 hover:border-purple-500/50 bg-purple-500/5'
                      }`}
                      data-testid={`platform-${platform.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`bg-gradient-to-r ${platform.color} w-10 h-10 rounded-lg flex items-center justify-center`}>
                          <span className="text-white font-bold text-sm">{platform.label.charAt(0)}</span>
                        </div>
                        <span className="text-white font-medium">{platform.label}</span>
                      </div>
                      {formData.platforms.includes(platform.id) && (
                        <CheckCircle2 className="h-6 w-6 text-purple-400" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-center text-purple-300/50 text-sm">
                  Platform connections coming soon - for now, we'll optimize content for your selected platforms
                </p>
              </div>
            )}

            {/* Step 3: Posting Goal */}
            {step === 3 && (
              <div className="space-y-6" data-testid="step-goal">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">How often do you want to post?</h2>
                  <p className="text-purple-200">Set a realistic goal to build your streak</p>
                </div>
                <div className="space-y-3">
                  {postingGoals.map((goal) => {
                    const Icon = goal.icon;
                    return (
                      <button
                        key={goal.id}
                        onClick={() => setFormData(prev => ({ ...prev, postingGoal: goal.id }))}
                        className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                          formData.postingGoal === goal.id
                            ? 'border-purple-500 bg-purple-500/20'
                            : 'border-purple-500/20 hover:border-purple-500/50 bg-purple-500/5'
                        }`}
                        data-testid={`goal-${goal.id}`}
                      >
                        <div className="bg-purple-500/30 p-3 rounded-lg">
                          <Icon className="h-6 w-6 text-purple-300" />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-medium">{goal.label}</p>
                          <p className="text-purple-300/70 text-sm">{goal.description}</p>
                        </div>
                        {formData.postingGoal === goal.id && (
                          <CheckCircle2 className="h-6 w-6 text-purple-400 ml-auto" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Experience Level */}
            {step === 4 && (
              <div className="space-y-6" data-testid="step-experience">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">What's your experience level?</h2>
                  <p className="text-purple-200">We'll tailor our suggestions to match your needs</p>
                </div>
                <div className="space-y-3">
                  {experienceLevels.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setFormData(prev => ({ ...prev, experience: level.id }))}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        formData.experience === level.id
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-purple-500/20 hover:border-purple-500/50 bg-purple-500/5'
                      }`}
                      data-testid={`experience-${level.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{level.label}</p>
                          <p className="text-purple-300/70 text-sm">{level.description}</p>
                        </div>
                        {formData.experience === level.id && (
                          <CheckCircle2 className="h-6 w-6 text-purple-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
                  <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-400" />
                    Your Profile Summary
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-purple-500/30 text-purple-200 border-purple-500/50">
                      {niches.find(n => n.id === formData.niche)?.label || formData.customNiche}
                    </Badge>
                    {formData.platforms.map(p => (
                      <Badge key={p} className="bg-blue-500/30 text-blue-200 border-blue-500/50">
                        {platforms.find(pl => pl.id === p)?.label}
                      </Badge>
                    ))}
                    <Badge className="bg-amber-500/30 text-amber-200 border-amber-500/50">
                      {postingGoals.find(g => g.id === formData.postingGoal)?.label}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 ? (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                  data-testid="back-button"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/daily-drop')}
                  className="text-purple-300 hover:text-white"
                  data-testid="skip-button"
                >
                  Skip for now
                </Button>
              )}
              
              {step < totalSteps ? (
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                  data-testid="next-button"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleComplete}
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
                  data-testid="complete-button"
                >
                  {isSubmitting ? 'Setting up...' : "Let's Go!"}
                  <Sparkles className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DailyDropOnboarding;
